import type { Tracker } from '../types';
import type { EventData } from '../utils/apTrackerHelpers';
import {
    isIgnoredEvent,
    convertLegacyEventName,
    isLibraryPage,
    mergeAdEvents,
} from '../utils/apTrackerHelpers';
import { getPostVars } from '../utils/getPostVars';
import { getMetaCookies } from '../utils/getMetaCookies';
import {
    hasClientSpecificVariant,
    isLeadEvent,
} from '../utils/conversionEvents';
import { APEventQueue } from '../apEventQueue';
import { getFacebookEvent } from './facebook-tracker/utils';
import { isServerForwardedEvent } from './facebook-tracker/serverForwardedEvents';

const ARCHIPRO_LOGGING_URL_V2 = '/api/analytics/v2/collect';

export function createArchiproTrackerV2(): Tracker {
    const queue = new APEventQueue(promiseExecutor());

    return {
        name: 'archiproTrackerV2',
        // eslint-disable-next-line @typescript-eslint/require-await
        async log(event, opts) {
            if (isIgnoredEvent(event)) {
                return;
            }

            // Don't mutate the original ones, it's shared between trackers
            const url = new URL(opts.url);

            // Resolved from the pre-rename event name so it matches what the
            // browser pixel sent. Carried in the payload rather than re-derived
            // downstream, because a name mismatch silently breaks Meta's
            // deduplication and double-counts the conversion.
            const facebookEvent = getFacebookEvent(event);
            const forwardToMeta =
                facebookEvent !== null &&
                isServerForwardedEvent(facebookEvent.eventName);

            // Also resolved before the rename below, which turns `Website` into
            // `website` and `PhoneReveal` into `Phone_reveal` — names the
            // classification does not recognise, so deferring this would report
            // two lead types as non-leads.
            const isLead = isLeadEvent(event);
            const clientSpecific =
                Boolean(opts.clientMatched) && hasClientSpecificVariant(event);

            event = convertLegacyEventName(event);

            const postVars = getPostVars(event, {
                url,
                data: opts.data,
                previousUrl: opts.previousUrl,
            });

            postVars.libraryPage = isLibraryPage(url.pathname) ? 1 : 0;

            if (forwardToMeta && facebookEvent && opts.eventId) {
                postVars.event_id = opts.eventId;
                postVars.fb_event_name = facebookEvent.eventName;

                const { fbp, fbc } = getMetaCookies();
                postVars.fbp = fbp;
                postVars.fbc = fbc;

                // Only the base event name is carried. The forwarder derives the
                // client-specific and lead events from these three facts, so
                // the two sides cannot drift apart over which names to send.
                //
                // `client_matched` therefore means "report a client-specific
                // copy of this", not merely "the professionals match". A page
                // view on the ad's professional's own page matches but is not a
                // conversion, and a client-specific copy of it would be a
                // server event with no browser event to pair with.
                postVars.conversion_professional_id = opts.professionalId;
                postVars.client_matched = clientSpecific ? 1 : 0;
                postVars.is_lead = isLead ? 1 : 0;
            }

            queue.queue(postVars);
            queue.startTimer();
        },
        release() {
            queue.flush();
        },
    };
}

function promiseExecutor(): (items: EventData[]) => Promise<void> {
    return async (items: EventData[]) => {
        try {
            const mergedItems = mergeAdEvents(items);
            await fetch(`${ARCHIPRO_LOGGING_URL_V2}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                },
                body: JSON.stringify(mergedItems),
                // Events flushed just before a navigation (login redirects,
                // post-auth reloads) must survive page unload.
                keepalive: true,
            });
        } catch {
            /**
             * these are connectivity errors, nothing to do.
             * Consider a retry perhaps
             */
            return;
        }
    };
}
